<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  AGENDA_BUCKETS,
  dateKey,
} from '../composables/postSaleAgendaPresenter';

const props = defineProps({
  days: { type: Array, default: () => [] },
  selectedDate: { type: Date, required: true },
});

const emit = defineEmits(['selectDate']);
const { t } = useI18n();
const carouselElement = ref(null);
const dayElements = new Map();
const weekdayLabels = computed(() => [
  t('POST_SALE.AGENDA.WEEKDAYS.SUNDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.MONDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.TUESDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.WEDNESDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.THURSDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.FRIDAY'),
  t('POST_SALE.AGENDA.WEEKDAYS.SATURDAY'),
]);
const monthDays = computed(() => props.days.filter(day => !day.outsideMonth));
const selectedKey = computed(() => dateKey(props.selectedDate));
const bucketClass = bucket =>
  ({
    amarelos: 'bg-[#FFFAF0]',
    laranjas: 'bg-[#FFF1E5]',
    vermelhos: 'bg-[#FCE9E9]',
  })[bucket];
const activeBuckets = day =>
  AGENDA_BUCKETS.filter(bucket => day.counts[bucket]);
const weekdayLabel = date => weekdayLabels.value[date.getDay()];
const setDayElement = (key, element) => {
  if (element) dayElements.set(key, element);
  else dayElements.delete(key);
};
const scrollToSelected = () => {
  nextTick(() => {
    const container = carouselElement.value;
    const selectedElement = dayElements.get(selectedKey.value);

    if (!container || !selectedElement) return;

    const targetLeft =
      selectedElement.offsetLeft -
      Math.max(0, (container.clientWidth - selectedElement.offsetWidth) / 2);

    container.scrollTo?.({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    });
  });
};

watch(selectedKey, scrollToSelected, { immediate: true });
</script>

<template>
  <div
    ref="carouselElement"
    data-testid="post-sale-agenda-day-carousel"
    class="min-w-0 max-w-full overflow-x-auto py-2"
  >
    <div class="flex min-w-max items-center justify-start gap-3 px-2">
      <div
        v-for="day in monthDays"
        :key="day.key"
        :ref="element => setDayElement(day.key, element)"
        class="w-[79px] shrink-0"
      >
        <div class="flex h-5 justify-center gap-2">
          <span
            v-for="bucket in activeBuckets(day)"
            :key="bucket"
            :data-bucket="bucket"
            class="h-[18px] rounded-[4px] px-0 text-center text-xs font-normal leading-[18px] text-[#000000DE]"
            :class="bucketClass(bucket)"
          >
            {{ day.counts[bucket] }}
          </span>
        </div>
        <button
          type="button"
          :data-day="day.day"
          class="flex h-14 w-full flex-col items-center justify-center gap-2 rounded-[10px] px-3.5 py-1 transition hover:bg-[#EDF8F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01988C]"
          :class="
            day.key === selectedKey
              ? 'bg-[#EDF8F7] text-[#01988C]'
              : 'text-lais-text-body'
          "
          :aria-pressed="day.key === selectedKey"
          @click="emit('selectDate', day.date)"
        >
          <span class="text-xs font-normal leading-[18px] uppercase">
            {{ weekdayLabel(day.date) }}
          </span>
          <span class="text-[20px] font-normal leading-6">{{ day.day }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
